import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Trash2, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/components/PropertyCard";

export default function MyDashboard() {
  const { user, loading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/properties/mine").then((r) => setProperties(r.data || [])).finally(() => setLoadingList(false));
  }, [user]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/properties/${id}`);
      setProperties((p) => p.filter((x) => x.id !== id));
      toast.success("Listing removed");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="overline mb-3">My Account</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900">Hello, {user.name?.split(" ")[0]}</h1>
        <p className="text-slate-600 mt-3 max-w-2xl font-light">Manage your listings, track inquiries, and update your profile.</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-3xl text-slate-900">Your Listings ({properties.length})</h2>
          <Link to="/post-property" data-testid="dash-add-listing" className="px-5 py-2.5 bg-[#D4A437] text-white text-sm hover:bg-[#D4A437] flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Listing
          </Link>
        </div>

        {loadingList ? (
          <div className="text-slate-500 py-12">Loading…</div>
        ) : properties.length === 0 ? (
          <div className="border border-slate-200 p-12 text-center">
            <h3 className="font-serif text-2xl text-slate-900 mb-3">No listings yet</h3>
            <p className="text-slate-600 mb-6">Post your first property in under 5 minutes.</p>
            <Link to="/post-property" className="inline-block px-6 py-3 bg-[#D4A437] text-white hover:bg-[#D4A437]" data-testid="dash-empty-cta">Post Property →</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {properties.map((p) => (
              <div key={p.id} className="border border-slate-200 bg-slate-50 p-6 flex gap-5" data-testid={`dash-listing-${p.id}`}>
                <div className="w-32 h-32 bg-slate-50 shrink-0 overflow-hidden">
                  {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="overline mb-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.locality}, {p.city}</div>
                  <h3 className="font-serif text-xl text-slate-900">{p.title}</h3>
                  <div className="text-[#D4A437] font-serif text-2xl mt-2">{formatPrice(p.price)}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.bhk} BHK · {p.area_sqft} sqft · {p.listing_type}</div>
                  <div className="mt-4 flex gap-3">
                    <button onClick={() => handleDelete(p.id)} data-testid={`del-${p.id}`} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
